import { useEffect, useMemo, useRef, useState } from 'react';
import { useBarsFetcher } from './useTradeStationQueries';
import {
  getRsTimeframe,
  normalizeRsTimeframeKey,
  resolveRsHistoryBarQuery,
} from '../constants/relativeStrengthTimeframes';
import {
  normalizeBarsForRelativeStrength,
  RS_VOLUME_METER_SCALE,
} from '../utils/relativeStrength';
import {
  averageRsSeries,
  buildRsSeries,
  equalWeightIndex,
  indexedCloses,
} from '../research/rsStudy/rsSeries';
import { RELATIVE_STRENGTH_MAX_TICKERS } from './useRelativeStrength';

function barsCacheKey(ticker, tf, query) {
  if (query.firstdate && query.lastdate) {
    return `${String(ticker).toUpperCase()}|hist|${tf.interval}|${tf.unit}|${query.firstdate}|${query.lastdate}`;
  }
  return `${String(ticker).toUpperCase()}|hist|${tf.interval}|${tf.unit}|${query.barsback}`;
}

const EMPTY = {
  loading: false,
  error: null,
  seriesByTicker: {},
  averageRs: [],
  groupIndex: [],
  benchmarkIndex: [],
};

/**
 * Rolling relative-strength history for the live table's tickers vs a benchmark.
 * Fetches a longer bar window than the live score so the chart can move over time.
 */
export function useRelativeStrengthHistory({
  tickers = [],
  relativeTo = 'SPY',
  timeframeKey = 'month',
  enabled = true,
} = {}) {
  const fetchBars = useBarsFetcher();
  const fetchBarsRef = useRef(fetchBars);
  fetchBarsRef.current = fetchBars;
  const barsCacheRef = useRef(new Map());

  const benchTicker = String(relativeTo || '').trim().toUpperCase();
  const tf = useMemo(
    () => getRsTimeframe(normalizeRsTimeframeKey(timeframeKey)),
    [timeframeKey],
  );
  const symbolList = useMemo(() => {
    const seen = new Set();
    const list = [];
    (tickers || []).forEach((raw) => {
      const sym = String(raw || '').trim().toUpperCase();
      if (!sym || seen.has(sym) || sym === benchTicker) return;
      seen.add(sym);
      list.push(sym);
    });
    return list.slice(0, RELATIVE_STRENGTH_MAX_TICKERS);
  }, [tickers, benchTicker]);

  const symbolKey = symbolList.join(',');
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (!enabled || !benchTicker || symbolList.length === 0) {
      setState(EMPTY);
      return undefined;
    }

    let cancelled = false;
    const query = resolveRsHistoryBarQuery(tf);
    const symbols = [benchTicker, ...symbolList];

    const load = async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      const barsByTicker = {};
      try {
        await Promise.all(symbols.map(async (ticker) => {
          const key = barsCacheKey(ticker, tf, query);
          if (barsCacheRef.current.has(key)) {
            barsByTicker[ticker] = barsCacheRef.current.get(key);
            return;
          }
          try {
            const data = await fetchBarsRef.current({
              ticker,
              interval: tf.interval,
              unit: tf.unit,
              sessionTemplate: 'USEQPreAndPost',
              ...query,
            });
            const bars = normalizeBarsForRelativeStrength(data?.Bars || [], tf.unit);
            barsCacheRef.current.set(key, bars);
            barsByTicker[ticker] = bars;
          } catch (err) {
            console.error(`Relative strength history bars failed for ${ticker}:`, err);
            barsCacheRef.current.set(key, []);
            barsByTicker[ticker] = [];
          }
        }));
        if (cancelled) return;
        const benchBars = barsByTicker[benchTicker] || [];
        const seriesByTicker = {};
        const priceBarsByTicker = {};
        symbolList.forEach((ticker) => {
          const tickerBars = barsByTicker[ticker] || [];
          priceBarsByTicker[ticker] = tickerBars;
          seriesByTicker[ticker] = buildRsSeries({
            tickerBars,
            benchBars,
            timeframe: tf,
            scale: RS_VOLUME_METER_SCALE,
          });
        });
        setState({
          loading: false,
          error: null,
          seriesByTicker,
          averageRs: averageRsSeries(Object.values(seriesByTicker)),
          groupIndex: equalWeightIndex(priceBarsByTicker),
          benchmarkIndex: indexedCloses(benchBars),
        });
      } catch (err) {
        if (!cancelled) {
          setState({
            ...EMPTY,
            error: err?.message || 'Failed to load relative strength history',
          });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, benchTicker, symbolKey, tf]);

  return state;
}

export default useRelativeStrengthHistory;
